import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { 
  CrudEntity, 
  CrudConfig, 
  CrudService, 
  CrudTableColumn, 
  CrudModalSection,
  CrudActionConfig 
} from '../../interfaces/crud-base.interface';

/**
 * Componente base reutilizable para vistas CRUD
 * Extiende este componente y proporciona la configuración para crear nuevas vistas CRUD
 */
@Component({
  selector: 'app-crud-base',
  templateUrl: './crud-base.component.html',
  styleUrls: ['./crud-base.component.scss']
})
export class CrudBaseComponent<T extends CrudEntity> implements OnInit {
  @Input() config!: CrudConfig;
  @Input() service!: CrudService<T>;

  // Estado del componente
  searchTerm: string = '';
  entities: T[] = [];
  filteredEntities: T[] = [];
  paginatedEntities: T[] = [];
  showEntityList: boolean = false;
  loading: boolean = false;
  error: string = '';
  
  // Paginación
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;
  totalItems: number = 0;
  itemsPerPageOptions: number[] = [5, 10, 25, 50];
  
  // Variables para el modal de detalles
  selectedEntity: T | null = null;
  loadingEntityDetails: boolean = false;

  constructor(private router: Router) { }

  ngOnInit(): void {
    if (!this.config || !this.service) {
      throw new Error('CrudBaseComponent requiere config y service como inputs');
    }
    this.loadEntities();
  }

  /**
   * Cargar entidades desde el backend
   */
  loadEntities(): void {
    this.loading = true;
    this.error = '';
    
    this.service.getAll().subscribe({
      next: (entities) => {
        this.entities = entities;
        this.filteredEntities = [...this.entities];
        this.updatePagination();
        this.loading = false;
      },
      error: (error) => {
        console.error(`Error al cargar ${this.config.entityNamePlural}:`, error);
        this.error = `Error al cargar los ${this.config.entityNamePlural}.`;
        this.loading = false;
      }
    });
  }

  /**
   * Buscar entidades
   */
  searchEntities(): void {
    if (this.searchTerm.trim() === '') {
      this.filteredEntities = [...this.entities];
      this.showEntityList = false;
    } else {
      this.service.search(this.searchTerm).subscribe({
        next: (entities) => {
          this.filteredEntities = entities;
          this.showEntityList = true;
          this.currentPage = 1;
          this.updatePagination();
        },
        error: (error) => {
          console.error(`Error al buscar ${this.config.entityNamePlural}:`, error);
          // Fallback a búsqueda local básica
          this.filteredEntities = this.entities.filter(entity => 
            this.performLocalSearch(entity, this.searchTerm)
          );
          this.showEntityList = true;
          this.currentPage = 1;
          this.updatePagination();
        }
      });
    }
  }

  /**
   * Búsqueda local básica (puede ser sobrescrita por componentes hijos)
   */
  protected performLocalSearch(entity: T, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase();
    return entity.id.toString().includes(term);
  }

  /**
   * Actualizar datos de paginación
   */
  updatePagination(): void {
    this.totalItems = this.filteredEntities.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    
    if (this.currentPage > this.totalPages) {
      this.currentPage = Math.max(1, this.totalPages);
    }
    
    this.updatePaginatedEntities();
  }

  /**
   * Actualizar entidades paginadas
   */
  updatePaginatedEntities(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedEntities = this.filteredEntities.slice(startIndex, endIndex);
  }

  /**
   * Cambiar página
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedEntities();
    }
  }

  /**
   * Cambiar items por página
   */
  changeItemsPerPage(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Obtener array de páginas para mostrar en la paginación
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfMax = Math.floor(maxPagesToShow / 2);
      let startPage = Math.max(1, this.currentPage - halfMax);
      let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
      
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  /**
   * Navegar a crear entidad
   */
  createEntity(): void {
    this.router.navigate([this.config.createRoute]);
  }

  /**
   * Ver detalles de la entidad
   */
  viewEntity(entity: T): void {
    this.selectedEntity = entity;
    this.loadingEntityDetails = true;
    
    this.service.getById(entity.id).subscribe({
      next: (entityDetails) => {
        this.selectedEntity = entityDetails;
        this.loadingEntityDetails = false;
        this.showEntityDetailsModal();
      },
      error: (error: any) => {
        console.error('Error al cargar detalles:', error);
        this.loadingEntityDetails = false;
        this.selectedEntity = entity; // Usar datos básicos si falla la carga
        this.showEntityDetailsModal();
      }
    });
  }

  /**
   * Mostrar modal de detalles
   */
  showEntityDetailsModal(): void {
    const modalElement = document.getElementById('entityDetailsModal');
    if (modalElement) {
      // Bootstrap modal show
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  /**
   * Editar entidad
   */
  editEntity(entity: T): void {
    this.router.navigate([`${this.config.editRoute}/${entity.id}`]);
  }

  /**
   * Eliminar entidad
   */
  deleteEntity(entity: T): void {
    const confirmMessage = `¿Está seguro de que desea eliminar este ${this.config.entityName}?`;
    
    if (confirm(confirmMessage)) {
      this.service.delete(entity.id).subscribe({
        next: (response: any) => {
          this.loadEntities(); // Recargar la lista
        },
        error: (error: any) => {
          console.error(`Error al eliminar ${this.config.entityName}:`, error);
          alert(`Error al eliminar el ${this.config.entityName}`);
        }
      });
    }
  }

  /**
   * Obtener valor formateado para mostrar en tabla
   */
  getFormattedValue(entity: T, column: CrudTableColumn): string {
    const value = this.getNestedValue(entity, column.key);
    
    if (column.formatter) {
      return column.formatter(value, entity);
    }
    
    switch (column.type) {
      case 'date':
        return this.formatDate(value);
      case 'text':
      default:
        return value?.toString() || 'N/A';
    }
  }

  /**
   * Obtener clase CSS para badges
   */
  getBadgeClass(entity: T, column: CrudTableColumn): string {
    if (column.badgeClass) {
      const value = this.getNestedValue(entity, column.key);
      return column.badgeClass(value, entity);
    }
    return 'bg-primary';
  }

  /**
   * Obtener valor anidado de un objeto usando notación de punto
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Formatear fecha
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return 'No especificado';
    }
    
    try {
      // Si la fecha viene en formato YYYY-MM-DD, parsearla directamente sin zona horaria
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month - 1 porque los meses en JS van de 0-11
        if (isNaN(date.getTime())) {
          return 'Fecha inválida';
        }
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Para otros formatos, usar el constructor normal
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }

  /**
   * Verificar si se debe mostrar un campo en el modal
   */
  shouldShowField(field: any, entity: T): boolean {
    if (field.condition) {
      return field.condition(entity);
    }
    return true;
  }

  /**
   * Obtener valor formateado para el modal
   */
  getModalFieldValue(entity: T, field: any): string {
    const value = this.getNestedValue(entity, field.key);
    
    if (field.formatter) {
      return field.formatter(value, entity);
    }
    
    switch (field.type) {
      case 'date':
        return this.formatDate(value);
      case 'text':
      default:
        return value?.toString() || 'No especificado';
    }
  }

  /**
   * Obtener clase CSS para badges en modal
   */
  getModalBadgeClass(entity: T, field: any): string {
    if (field.badgeClass) {
      const value = this.getNestedValue(entity, field.key);
      return field.badgeClass(value, entity);
    }
    return 'bg-primary';
  }

  /**
   * Alternar estado activo de una entidad
   */
  toggleEntityStatus(entity: T): void {
    // Verificar si la entidad tiene la propiedad is_active
    if (!('is_active' in entity)) {
      console.error('La entidad no tiene la propiedad is_active');
      return;
    }

    // Verificar si el servicio tiene el método toggleStudentStatus o toggleUserStatus
    const service = this.service as any;
    let toggleMethod: string | null = null;

    if (typeof service.toggleStudentStatus === 'function') {
      toggleMethod = 'toggleStudentStatus';
    } else if (typeof service.toggleUserStatus === 'function') {
      toggleMethod = 'toggleUserStatus';
    }

    if (!toggleMethod) {
      console.error('El servicio no tiene un método de toggle status implementado');
      return;
    }

    const entityWithActive = entity as any;
    const actionText = entityWithActive.is_active ? 'desactivar' : 'activar';
    const entityName = entityWithActive.name || 'elemento';
    const entityLastName = entityWithActive.last_name || '';
    const fullName = `${entityName} ${entityLastName}`.trim();
    const confirmMessage = `¿Está seguro de que desea ${actionText} a ${fullName}?`;
    
    if (confirm(confirmMessage)) {
      service[toggleMethod](entityWithActive.id).subscribe({
        next: (updatedEntity: any) => {
          // Actualizar la entidad en la lista local
          const entityIndex = this.entities.findIndex((e: any) => e.id === entityWithActive.id);
          if (entityIndex !== -1) {
            this.entities[entityIndex] = updatedEntity;
          }
          
          // Actualizar también en la lista filtrada si existe
          const filteredIndex = this.filteredEntities.findIndex((e: any) => e.id === entityWithActive.id);
          if (filteredIndex !== -1) {
            this.filteredEntities[filteredIndex] = updatedEntity;
          }
          
          // Actualizar paginación
          this.updatePagination();
          
          const statusText = updatedEntity.is_active ? 'activado' : 'desactivado';
          alert(`Elemento ${statusText} correctamente`);
        },
        error: (error: any) => {
          console.error('Error al cambiar estado:', error);
          alert('Error al cambiar el estado del elemento');
        }
      });
    }
  }

  /**
   * Obtener el estado activo de una entidad de manera segura
   */
  getEntityActive(entity: T): boolean {
    return (entity as any).is_active || false;
  }

  /**
   * Exponer Math para usar en el template
   */
  Math = Math;
}
